import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { EditorView } from "@codemirror/view";
// Using rehype-mathjax instead of better-react-mathjax for better markdown compatibility
import { apiClient } from "../apiClient";
import { useAuth } from "../hooks/useAuth";
import { useExercise } from "../hooks/useExercise";
import { useSubmissions } from "../hooks/useSubmissions";
import { useLanguage } from "../context/LanguageContext";
import { STATUS_COLORS } from "./statusColors";
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

function ExerciseDetail({ darkMode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { exercise, loading: exerciseLoading } = useExercise(id);
  const { submissions, createSubmission, refetch: refetchSubmissions } = useSubmissions();
  const [solution, setSolution] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [nextExerciseAvailable, setNextExerciseAvailable] = useState(true);
  const [showCode, setShowCode] = useState({});
  const [isRunning, setIsRunning] = useState(false);
  const [newSubmissionIndex, setNewSubmissionIndex] = useState(null);
  const { language } = useLanguage();

  // Filter submissions for current exercise
  const exerciseSubmissions = submissions.filter(
    sub => sub.exercise_id === parseInt(id)
  );

  // Update the solution when the exercise data changes
  useEffect(() => {
    if (exercise) {
      setSolution(
        exercise.sample_code ||
          `# Example function\ndef example():\n    return "Hello, World!"\n\nprint(example())`,
      );
      setNextExerciseAvailable(true);
    }
  }, [exercise]);

  const runSolution = async () => {
    setIsRunning(true);
    setNewSubmissionIndex(null); // Reset highlight effect
    try {
      console.log("Sending request to execute code...");
      
      const data = await apiClient.executeCode(solution, id);
      const status = data.success ? "Success" : "Failed";
      setTestResult(status);

      // Log submission
      if (user) {
        const submissionData = {
          exercise_id: parseInt(id),
          status,
          code: solution,
          trace: data.success
            ? `Test passed! Output: ${data.output}`
            : `Test failed. Output: ${data.output}, Expected: ${data.expected}`,
        };

        const { error } = await createSubmission(submissionData);
        
        if (error) {
          console.error("Error logging submission:", error);
        } else {
          setNewSubmissionIndex(0); // Highlight the newest submission
        }
      }
    } catch (error) {
      console.error("Error during code execution:", error);
      setTestResult("Error");
    } finally {
      setIsRunning(false);
    }
  };

  const goToNextExercise = async () => {
    try {
      const exercises = await apiClient.getExercises();
      const totalExercises = exercises.length;
      const nextId = parseInt(id) + 1;

      if (nextId <= totalExercises) {
        navigate(`/exercise/${nextId}`);
        setNextExerciseAvailable(true);
      } else {
        setNextExerciseAvailable(false);
      }
    } catch (error) {
      console.error("Error in goToNextExercise:", error);
      setNextExerciseAvailable(false);
    }
  };
  

  const goToPreviousExercise = () => {
    const previousId = parseInt(id) - 1;
    if (previousId > 0) {
      navigate(`/exercise/${previousId}`);
      setNextExerciseAvailable(true);
    } else {
      setNextExerciseAvailable(false);
    }
  };

  const downloadCode = (code) => {
    const blob = new Blob([code], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "submission.py";
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleCodeVisibility = (index) => {
    setShowCode((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  if (exerciseLoading || !exercise) return <div>Loading...</div>;
  
  // Simple content processing - database content should now be clean
  const processContent = (text) => {
    return text || "";
  };

  const markdownContent = processContent(language === "en" ? exercise.text_en : exercise.text_ru);
  const exampleContent = processContent(exercise.example_en || "");



  return (
    <div>
        <div>
          <button style={{ margin: "0 5px" }} onClick={() => navigate("/")}>
            Back to Exercises List
          </button>
          <button style={{ margin: "0 5px" }} onClick={goToPreviousExercise}>
            Previous Exercise
          </button>
          <button style={{ margin: "0 5px" }} onClick={goToNextExercise}>
            Next Exercise
          </button>
          {!nextExerciseAvailable && (
            <div style={{ color: "red", marginTop: "10px" }}>
              No more exercises available.
            </div>
          )}
          <h1>
            { exercise.title_en }
          </h1>
          <div className={`bubble ${darkMode ? "dark-mode" : ""}`}>
            <h3>Description</h3>
            {markdownContent ? (
              <ReactMarkdown
                remarkPlugins={[remarkMath, remarkGfm]}
                rehypePlugins={[rehypeKatex]}
              >
                {markdownContent}
              </ReactMarkdown>
            ) : (
              <p style={{color: 'red'}}>No description content available</p>
            )}
          </div>
          <div className={`bubble ${darkMode ? "dark-mode" : ""}`}>
            <h3>Test Cases</h3>
            {exampleContent ? (
              <ReactMarkdown
                remarkPlugins={[remarkMath, remarkGfm]}
                rehypePlugins={[rehypeKatex]}
              >
                {exampleContent}
              </ReactMarkdown>
            ) : (
              <p style={{color: 'red'}}>No test cases content available</p>
            )}
          </div>
        <h3>Code Editor</h3>
        <CodeMirror
          value={solution}
          extensions={[python(), EditorView.theme({}, { dark: darkMode })]}
          className={darkMode ? "cm-dark-theme" : "cm-light-theme"}
          onChange={(value) => {
            setSolution(value);
          }}
        />
        <button onClick={runSolution} disabled={isRunning}>
          {isRunning ? "Running..." : "Run"}
        </button>
        {testResult && (
          <div style={{ marginTop: "10px", ...STATUS_COLORS[testResult] }}>
            Result: {testResult}
          </div>
        )}
        <h3>Submissions</h3>
        <table>
          <thead>
            <tr>
              <th>Status</th>
              <th>Submission Time</th>
              <th>Trace</th>
              <th>Download Code</th>
              <th>Show Code</th>
            </tr>
          </thead>
          <tbody>
            {exerciseSubmissions.map((submission, index) => (
              <tr key={index} className={index === newSubmissionIndex ? "new-submission" : ""}>
                <td style={STATUS_COLORS[submission.status] || {}}>{submission.status}</td>
                <td>
                  {new Date(submission.created_at).toLocaleString()}
                </td>
                <td>{submission.trace}</td>
                <td>
                  <button onClick={() => downloadCode(submission.code)}>
                    Download
                  </button>
                </td>
                <td>
                  <button onClick={() => toggleCodeVisibility(index)}>
                    {showCode[index] ? "Hide Code" : "Show Code"}
                  </button>
                  {showCode[index] && (
                    <textarea
                      readOnly
                      value={submission.code}
                      style={{
                        width: "100%",
                        height: "100px",
                        marginTop: "10px",
                      }}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ExerciseDetail;
